import { DatePipe } from '@angular/common';
import { Component, OnDestroy, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  CommentsPostDisplayPaginationAllInOneComponent,
  ConversationApiService,
  ConversationCommentListQuery,
  ConversationDefinition,
  ConversationStateService,
  InsightMarkdownPipe,
  PaginatedComments,
} from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../auth';
import { LanguageService, reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { PageTitleService } from '../../navigation/title/title.service';
import { FeatureRequest, FeatureRequestError, featureRequestStageBadge, featureRequestStatusKey } from '../feature-request.model';
import { FeatureRequestService } from '../feature-request.service';
import { ideasBaseRoute, ideasHubSlug, nameHubInBreadcrumb } from '../ideas-context';

const COMMENTS_PER_PAGE = 10;

/**
 * One idea in full: the initiator's own words, what we understood and
 * delivered, our answer, the like, and the discussion.
 *
 * **One plain like per reader** — the Ideas plan, decision 3.8, as partly
 * withdrawn on 2026-10-01.
 *
 * **Nobody is named for an idea entered on somebody's behalf**: the page shows
 * a phrase from its origin instead, and the initiator's name only for an idea
 * they wrote themselves — decision 3.19.
 *
 * **The discussion is a conversation in the reader's language**, created by
 * its first comment, as on tips — decision 3.21 — *Comments are
 * conversations, one per request per locale*. A pending request has none.
 *
 * **A reader may follow it**, to hear of its status changes by email and of
 * new comments in the bell; submitting follows it — decision 3.29 — *Who is
 * notified of what, and following is an explicit act*.
 *
 * **The initiator may withdraw a pending request**, and may start again from
 * its words: the submit form opens prefilled — decision 3.20.
 */
@Component({
  selector: 'multisite-idea-detail',
  templateUrl: './idea-detail.component.html',
  imports: [DatePipe, RouterModule, TranslocoModule, InsightMarkdownPipe, LoaderGlobeAnimationDetailComponent, CommentsPostDisplayPaginationAllInOneComponent],
  providers: [provideTranslocoScope('ideas')],
})
export class IdeaDetailComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private ideas = inject(FeatureRequestService);
  private authService = inject(AuthService);
  private languageService = inject(LanguageService);
  private conversationApi = inject(ConversationApiService);
  private conversationState = inject(ConversationStateService);
  private pageTitleService = inject(PageTitleService);

  readonly hub = ideasHubSlug(this.route);
  readonly statusKey = featureRequestStatusKey;
  /** The status badge in its stage's colour, as on the listing. */
  readonly stageBadge = featureRequestStageBadge;
  private readonly nameHub = nameHubInBreadcrumb(this.hub);
  readonly baseRoute = ideasBaseRoute(this.hub);
  readonly user = toSignal(this.authService.user);

  idea = signal<FeatureRequest | null>(null);
  failure = signal<FeatureRequestError | null>(null);
  actionFailure = signal<FeatureRequestError | null>(null);
  loading = signal(false);
  liking = signal(false);
  changingFollow = signal(false);
  /** A translation into the reader's language has been asked for and has not settled. */
  translating = signal(false);
  withdrawing = signal(false);
  confirmingWithdrawal = signal(false);

  paginatedComments = signal<PaginatedComments | undefined>(undefined);
  loadingComments = signal(false);
  clearCommentForm = signal(false);
  conversationId = signal<number | null>(null);
  conversationName = signal<string | undefined>(undefined);


  private slug: string = this.route.snapshot.paramMap.get('slug') ?? '';
  private subscriptions: Subscription[] = [];
  private translation?: Subscription;

  constructor() {
    this.load();
    reloadOnLocaleChange(() => {
      this.conversationId.set(null);
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.translation?.unsubscribe();
  }

  toggleLike(): void {
    const idea = this.idea();

    if (!idea?.can.like || this.liking()) {
      return;
    }

    this.liking.set(true);
    this.actionFailure.set(null);

    this.subscriptions.push(
      this.ideas
        .like(idea.slug, !idea.liked)
        .pipe(finalize(() => this.liking.set(false)))
        .subscribe({
          // The like's answer carries no related records; keep the ones the page has.
          next: (updated) => this.idea.set({ ...updated, topics: idea.topics, tags: idea.tags, cultures: idea.cultures }),
          error: (failure: FeatureRequestError) => this.actionFailure.set(failure),
        }),
    );
  }

  /** Follow, or stop: status changes by email, comments in the bell. */
  toggleFollow(): void {
    const idea = this.idea();

    if (!idea?.can.follow || this.changingFollow()) {
      return;
    }

    this.changingFollow.set(true);
    this.actionFailure.set(null);

    this.subscriptions.push(
      this.ideas
        .follow(idea.slug, !idea.following)
        .pipe(finalize(() => this.changingFollow.set(false)))
        .subscribe({
          // The answer carries no related records; keep the ones the page has.
          next: (updated) => this.idea.set({ ...updated, topics: idea.topics, tags: idea.tags, cultures: idea.cultures }),
          error: (failure: FeatureRequestError) => this.actionFailure.set(failure),
        }),
    );
  }

  /** Withdraw, and optionally open the submit form with the same words. */
  withdraw(startAgain: boolean): void {
    const idea = this.idea();

    if (!idea || this.withdrawing()) {
      return;
    }

    this.withdrawing.set(true);
    this.actionFailure.set(null);

    this.subscriptions.push(
      this.ideas
        .withdraw(idea.slug)
        .pipe(finalize(() => this.withdrawing.set(false)))
        .subscribe({
          next: () => {
            if (startAgain) {
              this.router.navigate([...this.baseRoute, 'new'], {
                state: { ideaDraft: { name: idea.name ?? '', originalRequest: idea.originalRequest ?? '' } },
              });
            } else {
              this.router.navigate(this.baseRoute, { queryParams: { withdrawn: idea.slug } });
            }
          },
          error: (failure: FeatureRequestError) => this.actionFailure.set(failure),
        }),
    );
  }

  getComments(page: number): void {
    const conversationId = this.conversationId();

    if (conversationId) {
      this.loadComments(conversationId, page);
      return;
    }

    this.previewConversation(page);
  }

  postComment(message: string): void {
    const conversationId = this.conversationId();
    const page = this.paginatedComments()?.meta.current_page ?? 1;
    const onPosted = () => {
      this.clearCommentForm.update((value) => !value);
      this.getComments(page);
    };

    if (conversationId) {
      this.subscriptions.push(this.conversationApi.createCommentInConversation(conversationId, message).subscribe({ next: onPosted }));
      return;
    }

    this.subscriptions.push(
      this.conversationState.submitFirstCommentByDefinition(this.definition(), message, this.commentQuery(page)).subscribe({
        next: (result) => {
          this.conversationId.set(result.conversationId);
          this.paginatedComments.set(result.comments);
          this.clearCommentForm.update((value) => !value);
        },
      }),
    );
  }

  private load(): void {
    this.loading.set(true);
    this.failure.set(null);

    this.subscriptions.push(
      this.ideas
        .getOne(this.slug)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: (idea) => {
            this.idea.set(idea);
            this.pageTitleService.setTitle(idea.name ?? '');

            if (this.hub) {
              this.nameHub(idea.hub?.name);
            }

            if (!idea.pending) {
              this.getComments(1);
            }

            this.translateIfNeeded(idea);
          },
          error: (failure: FeatureRequestError) => this.failure.set(failure),
        }),
    );
  }

  /**
   * An idea not yet in the reader's language is asked for, and the text swapped in once the
   * translation settles, as news does — the Ideas plan, decision 3.31. A `partial` or
   * `refused` translation is not asked for again: nothing further would happen.
   */
  private translateIfNeeded(idea: FeatureRequest): void {
    this.translation?.unsubscribe();

    if (FeatureRequestService.needsTranslation(idea) && idea.requestedLocale) {
      this.translating.set(true);
      this.translation = this.ideas.requestTranslation(idea.slug, idea.requestedLocale).subscribe({
        next: (answer) => this.followTranslation(answer),
        error: () => this.translating.set(false),
      });
    } else {
      this.followTranslation(idea, false);
    }
  }

  /** Show what arrived, and keep watching while it is worth waiting. */
  private followTranslation(answer: FeatureRequest, show = true): void {
    if (show) {
      this.showTranslated(answer);
    }

    if (!answer.worthPolling) {
      this.translating.set(false);
      return;
    }

    this.translating.set(true);
    this.translation = this.ideas
      .watchTranslation(answer.slug)
      .pipe(finalize(() => this.translating.set(false)))
      .subscribe((settled) => this.showTranslated(settled));
  }

  /** The trigger's answer carries no related records; keep the ones the page has. */
  private showTranslated(answer: FeatureRequest): void {
    const current = this.idea();

    this.idea.set(current ? { ...answer, topics: current.topics, tags: current.tags, cultures: current.cultures } : answer);
    this.pageTitleService.setTitle(answer.name ?? '');
  }

  private previewConversation(page: number): void {
    this.loadingComments.set(true);

    this.subscriptions.push(
      this.conversationApi.previewConversation(this.definition()).subscribe({
        next: (preview) => {
          const id = (preview as { id?: unknown })?.id;
          this.conversationName.set((preview as { name?: string })?.name);

          if (typeof id === 'number') {
            this.conversationId.set(id);
            this.loadComments(id, page);
          } else {
            this.paginatedComments.set(undefined);
            this.loadingComments.set(false);
          }
        },
        error: () => this.loadingComments.set(false),
      }),
    );
  }

  private loadComments(conversationId: number, page: number): void {
    this.loadingComments.set(true);

    this.subscriptions.push(
      this.conversationApi
        .listCommentsByConversationId(conversationId, this.commentQuery(page))
        .pipe(finalize(() => this.loadingComments.set(false)))
        .subscribe({ next: (comments) => this.paginatedComments.set(comments) }),
    );
  }

  private definition(): ConversationDefinition {
    const id = this.idea()?.id;

    return {
      locale: this.languageService.activeLanguage()?.languageKey || 'en',
      category: null,
      type: null,
      feature_requests: typeof id === 'number' ? [id] : [],
    };
  }

  private commentQuery(page: number): ConversationCommentListQuery {
    return { page, per_page: COMMENTS_PER_PAGE, sort_by: 'created_at', sort_direction: 'desc' };
  }
}
